/**
 * ChallengeContext - Contexto para gerenciar desafio ativo
 * 
 * Rastreia qual desafio o usuário está participando ativamente
 * e sincroniza pontuação das questões resolvidas com o Supabase
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import { updateChallengeProgress } from '../services/challengesService';

const ChallengeContext = createContext(null);

export const ChallengeProvider = ({ children }) => {
    // Estado do desafio ativo
    const [activeChallenge, setActiveChallenge] = useState(null);

    /**
     * Inicia um desafio - chamado ao clicar "Iniciar Desafio"
     * @param {Object} challenge - Dados do desafio
     */
    const startChallenge = useCallback((challenge) => {
        setActiveChallenge({
            id: challenge.id,
            title: challenge.title || challenge.titulo,
            disciplina: challenge.disciplina || challenge.subject,
            assunto: challenge.assunto,
            startedAt: new Date().toISOString(),
        });

    }, []);

    /**
     * Finaliza o desafio ativo
     */
    const endChallenge = useCallback(() => {
        if (activeChallenge) {

        }
        setActiveChallenge(null);
    }, [activeChallenge]);

    /**
     * Verifica se uma categoria/disciplina corresponde ao desafio ativo
     * @param {string} category - Categoria da questão respondida
     * @returns {boolean}
     */
    const shouldTrackForChallenge = useCallback((category) => {
        if (!activeChallenge || !category) return false;

        const challengeDisciplina = activeChallenge.disciplina?.toLowerCase() || '';
        const questionCategory = category.toLowerCase();

        // Mapeamento de disciplinas para categorias do sistema
        const categoryMappings = {
            'português': ['portugues', 'português', 'gramatica', 'gramática', 'interpretacao', 'interpretação'],
            'matemática': ['matematica', 'matemática', 'raciocinio', 'raciocínio', 'matematica_financeira'],
            'informática': ['informatica', 'informática', 'conceitos', 'tecnologia'],
            'direito constitucional': ['direito', 'constitucional', 'direito_constitucional'],
            'conhecimentos bancários': ['conhecimentos_bancarios', 'bancarios', 'conhecimentos bancários'],
        };

        // Verifica correspondência direta
        if (questionCategory.includes(challengeDisciplina) || challengeDisciplina.includes(questionCategory)) {
            return true;
        }

        // Verifica mapeamento
        const mappedCategories = categoryMappings[challengeDisciplina] || [];
        return mappedCategories.some(mapped =>
            questionCategory.includes(mapped) || mapped.includes(questionCategory)
        );
    }, [activeChallenge]);

    /**
     * Registra uma resposta para o desafio ativo (se aplicável)
     * @param {string} category - Categoria da questão
     * @param {boolean} isCorrect - Se acertou
     * @param {string} userId - ID do usuário
     * @returns {Promise<boolean>} - Se foi contabilizado para o desafio
     */
    const trackChallengeAnswer = useCallback(async (category, isCorrect, userId) => {
        if (!activeChallenge || !userId) return false;

        // Verifica se a categoria corresponde ao desafio
        if (!shouldTrackForChallenge(category)) {

            return false;
        }

        try {
            // Atualiza progresso no Supabase
            const result = await updateChallengeProgress(
                activeChallenge.id,
                userId,
                isCorrect
            );

            if (result.success) {

                return true;
            } else {

                return false;
            }
        } catch (error) {

            return false;
        }
    }, [activeChallenge, shouldTrackForChallenge]);

    /**
     * Verifica se há um desafio ativo
     */
    const hasActiveChallenge = !!activeChallenge;

    const value = {
        activeChallenge,
        hasActiveChallenge,
        startChallenge,
        endChallenge,
        shouldTrackForChallenge,
        trackChallengeAnswer,
    };

    return (
        <ChallengeContext.Provider value={value}>
            {children}
        </ChallengeContext.Provider>
    );
};

/**
 * Hook para acessar o contexto do desafio
 */
export const useChallenge = () => {
    const context = useContext(ChallengeContext);
    if (!context) {
        throw new Error('useChallenge deve ser usado dentro de ChallengeProvider');
    }
    return context;
};

export default ChallengeContext;
